// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Click-to-define terms.
 *
 * The audience is researchers and journalists, not typesetters, so "folio",
 * "text layer" and "pinpoint" are jargon here even though they are ordinary
 * words in publishing. A tool that reports a verdict in words the reader has to
 * look up somewhere else has not really reported anything.
 */

export const GLOSSARY: Record<string, string> = {
  folio:
    'The page number printed on the page itself. It is often not the same as the sheet number: a journal article downloaded as a PDF may have 11 sheets whose printed folios run 818 to 828.',
  'text layer':
    'The characters stored inside a PDF alongside the picture of the page. A file made from a word processor has one; a file made by photographing or scanning pages usually has none, and there is nothing in it to search.',
  pinpoint:
    'The exact place inside a source that a citation points to — "p. 14", "at 1207", "¶ 32" — as opposed to the source as a whole.',
  'semi-global alignment':
    'Lining up two pieces of text where one is allowed to start and stop anywhere but the other has to match all the way through. It is what stops a twenty-word quotation with one word changed being reported as a perfect match on its first three words.',
  'n-gram':
    'A run of n consecutive words. Indexing every five-word run of every source is what narrows a quotation down to a handful of places worth examining closely, out of millions of possible positions.',
  ligature:
    'Two or more letters drawn as one glyph — ﬁ, ﬂ, ﬀ. PDFs store them as a single character, which is why searching a PDF for "find" sometimes fails on a word that is plainly there.',
  'running head':
    'The repeated line of furniture at the top or bottom of every page — the chapter title, the journal name, the page number. It sits in the text of the file, so it has to be removed before a quotation that runs across a page break can be matched.',
  DOI: 'A Digital Object Identifier: a permanent code such as 10.1371/journal.pbio.0020449 that resolves to one specific published work.',
  'orthographic difference':
    'A difference of spelling only — organise against organize, Smith against Smyth. It is a house-style difference, not a misquotation, and it is labelled separately for that reason.',
};

const TIP_ID = 'glossary-tip';

let tooltip: HTMLElement | null = null;
let describedLink: HTMLElement | null = null;

function ensureTooltip(): HTMLElement {
  if (tooltip) return tooltip;
  tooltip = document.createElement('div');
  tooltip.className = 'glossary-tip';
  tooltip.id = TIP_ID;
  tooltip.hidden = true;
  tooltip.setAttribute('role', 'tooltip');
  document.body.appendChild(tooltip);
  return tooltip;
}

function hide(): void {
  if (tooltip) tooltip.hidden = true;
  // The description has to come off the term as well as the tip going away:
  // a stale aria-describedby leaves a screen reader reading last term's
  // definition after the tooltip has visually gone.
  describedLink?.removeAttribute('aria-describedby');
  describedLink = null;
}

function show(link: HTMLElement): void {
  const tip = ensureTooltip();
  const key = link.dataset.term ?? link.textContent?.trim() ?? '';
  const definition = GLOSSARY[key.toLowerCase()] ?? GLOSSARY[key];
  if (!definition) return;
  tip.textContent = definition;
  tip.hidden = false;
  describedLink?.removeAttribute('aria-describedby');
  link.setAttribute('aria-describedby', TIP_ID);
  describedLink = link;
  const box = link.getBoundingClientRect();
  const width = Math.min(320, window.innerWidth - 24);
  tip.style.width = `${width}px`;
  const left = Math.max(12, Math.min(box.left, window.innerWidth - width - 12));
  tip.style.left = `${left}px`;
  // Flip above the term when there is no room beneath it.
  const below = box.bottom + 8;
  if (below + tip.offsetHeight > window.innerHeight - 12) {
    tip.style.top = `${Math.max(12, box.top - tip.offsetHeight - 8)}px`;
  } else {
    tip.style.top = `${below}px`;
  }
}

export function initGlossary(): void {
  const tip = ensureTooltip();

  document.addEventListener('pointerdown', (event) => {
    const target = event.target as HTMLElement;
    const link = target.closest<HTMLElement>('.glossary-link');
    if (!link) {
      if (!tip.contains(target)) hide();
      return;
    }
    event.preventDefault();
    show(link);
  });

  /*
   * Keyboard activation, which pointerdown does not cover.
   *
   * A <span role="button"> gets no activation behaviour from the browser at
   * all — unlike a real <button> it does not even synthesise a click — so
   * before this handler existed, a term that announced itself as a button did
   * nothing on Enter, and Space fell through to scrolling the page. `keydown`
   * rather than `click` deliberately: adding a click listener beside the
   * pointerdown one would fire both on a real tap.
   */
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      hide();
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target as HTMLElement | null;
    if (!target || typeof target.closest !== 'function') return;
    const link = target.closest<HTMLElement>('.glossary-link');
    if (!link) return;
    event.preventDefault();
    if (link === describedLink && !tip.hidden) hide();
    else show(link);
  });
  window.addEventListener('resize', hide);
  window.addEventListener('scroll', hide, true);
}

/** Wrap a known term so it becomes clickable. */
export function term(text: string, key = text): string {
  return `<span class="glossary-link" data-term="${key}" role="button" tabindex="0">${text}</span>`;
}
